import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { GeneralCbiEntityInfo } from '@cbinsights/cbientityservice/cbientityservice';
import { LiveSearchType } from '@cbinsights/integratedsearchservice/integratedsearchservice';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Input,
  List,
  Modal,
  Tag,
  Typography,
  message,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import useHasEditAccess from 'client/modules/users/hooks/useHasEditAccess';
import { updateTeamEntity } from '../services/api/UpdateTeamEntity';
import { updateUserEntity } from '../services/api/UpdateUserEntity';
import { useGetLiveSearch } from '../services/hooks/useGetLiveSearch';

const { Title } = Typography;

type EntityLinkingProps = {
  entity: GeneralCbiEntityInfo | null; // Allow null for no linked entity
  onEntityChange: () => void; // Callback for updating the linked entity
  isLoading: boolean;
  idTeam?: number;
  idUser?: number;
};

const EntityLinking: React.FC<EntityLinkingProps> = ({
  entity,
  onEntityChange,
  isLoading,
  idTeam,
  idUser,
}) => {
  const [isDrawerVisible, setDrawerVisible] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityList, setEntityList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState<{
    id: number;
    name: string;
  }>({ id: 0, name: '' });
  const [isSaveDisabled, setSaveDisabled] = useState(true);
  const [initiateSearch, setInitiateSearch] = useState(false);

  const handleOpenDrawer = () => setDrawerVisible(true);
  const handleCloseDrawer = () => setDrawerVisible(false);

  const handleOpenModal = () => setModalVisible(true);
  const handleCloseModal = () => {
    setModalVisible(false);
    setSearchTerm('');
    setEntityList([]);
    setSelectedEntity({ id: 0, name: '' });
    setSaveDisabled(true);
    setInitiateSearch(false);
  };

  const updateTeamEntityMutation = useMutation(updateTeamEntity);
  const updateUserEntityMutation = useMutation(updateUserEntity);

  const isLoadingUpdateEntity =
    updateTeamEntityMutation.isLoading || updateUserEntityMutation.isLoading;

  const handleSearch = () => {
    if (searchTerm) {
      setInitiateSearch(true);
    }
  };

  const handleEntitySelect = (newEntity: { id: number; name: string }) => {
    setSelectedEntity(newEntity);
    setSearchTerm('');
    setEntityList([]);
    setSaveDisabled(false);
    setInitiateSearch(false);
  };

  const handleEntitySubmit = () => {
    if (idTeam) {
      updateTeamEntityMutation
        .mutateAsync({ id_entity: selectedEntity.id, id_team: idTeam })
        .then(() => {
          message.success('Entity linked successfully');
          onEntityChange();
          handleCloseModal();
        })
        .catch(() => {
          message.error('Failed to link entity. Please try again.');
        });
    }

    if (idUser) {
      updateUserEntityMutation
        .mutateAsync({ id_entity: selectedEntity.id, id_user: idUser })
        .then(() => {
          message.success('Entity linked successfully');
          onEntityChange();
          handleCloseModal();
        })
        .catch(() => {
          message.error('Failed to link entity. Please try again.');
        });
    }
  };

  const hasEditAccess = useHasEditAccess();

  const { data: liveSearch, isLoading: isLoadingLiveSearch } = useGetLiveSearch(
    {
      enabled: initiateSearch,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      params: { prefix: searchTerm, size: 10, typeFilters: [{ type: 1 }] },
    }
  );

  useEffect(() => {
    if (liveSearch?.suggestionGroups) {
      const entities = [];

      liveSearch.suggestionGroups.forEach((group) => {
        if (group.type === LiveSearchType.ORG) {
          group.suggestions.forEach((suggestion) => {
            entities.push({
              name: suggestion.org.name,
              id: suggestion.org.id_org,
            });
          });
        }
      });
      setEntityList(entities);
    }
  }, [liveSearch]);

  useEffect(() => {
    if (isModalVisible) {
      setSearchTerm('');
      setEntityList([]);
    }
  }, [isModalVisible]);

  const isLoadingAddEntity =
    updateTeamEntityMutation.isLoading ||
    updateUserEntityMutation.isLoading ||
    isLoadingLiveSearch;

  return (
    <>
      {entity?.idCbiEntity && !isLoading && (
        <Button type="link" className="mr-2" onClick={handleOpenDrawer}>
          {entity.entityNameInfo.primaryName || entity.idCbiEntity}
        </Button>
      )}

      <Button
        type="default"
        shape="circle"
        icon={<EditOutlined style={{ fontSize: '14px' }} />}
        onClick={handleOpenModal}
        loading={isLoading}
        disabled={!hasEditAccess}
      />

      {entity && (
        <Drawer
          title={`Entity Details - ${
            entity?.entityNameInfo?.primaryName || 'Unknown'
          }`}
          placement="right"
          onClose={handleCloseDrawer}
          open={isDrawerVisible}
          width={600}
        >
          <Title level={4}>General Information</Title>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Description">
              {entity.description}
            </Descriptions.Item>
            <Descriptions.Item label="Email">{entity.email}</Descriptions.Item>
            <Descriptions.Item label="Status">
              {entity.status?.status || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Founded Year">
              {entity.foundedYear}
            </Descriptions.Item>
            <Descriptions.Item label="Total Funding">
              {entity.totalFunding.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="VC Backed">
              <Tag color={entity.vcBacked ? 'green' : 'red'}>
                {entity.vcBacked ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>

          <Title level={4} style={{ marginTop: '16px' }}>
            Related Entities
          </Title>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Parent Entities">
              {entity.parentEntities.map((parent) => (
                <Tag key={parent.idCbiEntity}>
                  {parent.entityNameInfo.primaryName}
                </Tag>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="Child Entities">
              {entity.childEntities.map((child) => (
                <Tag key={child.idCbiEntity}>
                  {child.entityNameInfo.primaryName}
                </Tag>
              ))}
            </Descriptions.Item>
          </Descriptions>
        </Drawer>
      )}

      <Modal
        title="Select Entity"
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleEntitySubmit}
        okText="Save"
        okButtonProps={{
          disabled: isSaveDisabled || isLoadingUpdateEntity,
          loading: isLoadingUpdateEntity,
        }}
        cancelButtonProps={{
          disabled: isLoadingUpdateEntity,
          loading: isLoadingUpdateEntity,
        }}
      >
        {idUser &&
          (entity?.idCbiEntity || entity?.entityNameInfo?.primaryName) && (
            <div>
              <Alert
                type="info"
                className="mb-5"
                message="By reassigning a user to a new CBI entity we will regenerate markets, generated for you watchlists (keeping companies the user added), topics (keeping topics the user added), autonomous insights, and a personal briefing"
              />
            </div>
          )}

        {selectedEntity.id > 0 && (
          <div className="flex justify-between mb-2">
            <span>Selected Entity: {selectedEntity.name}</span>
          </div>
        )}

        <Input.Search
          placeholder="Search for an entity (e.g. Microsoft or microsoft.com)"
          onSearch={handleSearch}
          enterButton
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {isLoadingAddEntity && (
          <div className="flex justify-center mt-4">
            <LoadingOutlined size={10} />
          </div>
        )}

        {initiateSearch && !isLoadingAddEntity && (
          <List
            className="mt-2"
            dataSource={entityList}
            renderItem={(item) => (
              <List.Item
                className="flex justify-between"
                onClick={() => handleEntitySelect(item)}
                style={{ cursor: 'pointer' }}
              >
                <span>{item.name}</span>
                <span>{item.id}</span>
              </List.Item>
            )}
          />
        )}
        {idTeam && (
          <Alert
            className="mt-4"
            message="Changing this information will propagate down to all users on the team."
            type="warning"
            showIcon
          />
        )}
      </Modal>
    </>
  );
};

export default EntityLinking;
